import React, { useLayoutEffect, useRef } from "react";
import "./SourceQuickAccess.css";
import { toPersianDigits } from "../../utils/toPersianDigits";
import { getSourceMapValue } from "../../utils/sourceModel";
import RetryLogoImage from "../media/RetryLogoImage";

const DefaultSourceIcon = ({ name = "" }) => (
  <span className="quick-access-icon default-icon" aria-hidden="true">
    {name.charAt(0) || "؟"}
  </span>
);

const SourceQuickAccess = ({
  allSources = [],
  activeSources = [],
  focusSource,
  onSourceFocus,
  favicons = {},
  badgeCounts = {},
  isSourcePanelOpen = false,
  onToggleSourcePanel,
  isFocusMode = false,
}) => {
  const buttonRefs = useRef({});

  useLayoutEffect(() => {
    if (
      !focusSource ||
      typeof window === "undefined" ||
      !window.matchMedia("(min-width: 768px)").matches
    ) {
      return undefined;
    }

    const scrollFocusedButtonIntoView = () => {
      const button = buttonRefs.current[focusSource];

      if (button) {
        button.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    };

    scrollFocusedButtonIntoView();

    const frameId = window.requestAnimationFrame(scrollFocusedButtonIntoView);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [allSources, focusSource]);

  return (
    <div
      className={`source-quick-access${
        isFocusMode ? " source-quick-access--hidden" : ""
      }`}
      aria-hidden={isFocusMode}
    >
      <div className="quick-access-list">
        {allSources.map((source) => {
          const sourceName = toPersianDigits(source);
          const isFocused = focusSource === source;
          const anyFocused = Boolean(focusSource);
          const isActive = activeSources.includes(source);
          const badge = getSourceMapValue(badgeCounts, source, 0) || 0;

          let wrapperClass = "quick-access-item-wrapper";

          if (isFocused) {
            wrapperClass += " quick-access-item-wrapper--focused";
          } else if (anyFocused) {
            wrapperClass += " quick-access-item-wrapper--dimmed";
          } else if (!isActive) {
            wrapperClass += " quick-access-item-wrapper--inactive";
          }

          return (
            <div key={source} className={wrapperClass}>
              <button
                ref={(node) => {
                  if (node) {
                    buttonRefs.current[source] = node;
                  } else {
                    delete buttonRefs.current[source];
                  }
                }}
                type="button"
                className={`quick-access-btn${
                  isFocused ? " quick-access-btn--focused" : ""
                }`}
                onClick={() => {
                  if (isActive) {
                    onSourceFocus?.(source);
                  }
                }}
                disabled={!isActive}
                title={sourceName}
                aria-label={sourceName}
              >
                {favicons[source] ? (
                  <RetryLogoImage
                    src={favicons[source]}
                    alt=""
                    className="quick-access-icon"
                    fallbackText={source.charAt(0) || "؟"}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <DefaultSourceIcon name={source} />
                )}

                {badge > 0 && (
                  <span className="unread-badge">
                    {toPersianDigits(badge)}
                  </span>
                )}
              </button>
            </div>
          );
        })}

        {onToggleSourcePanel && (
          <div className="quick-access-item-wrapper quick-access-panel-toggle">
            <button
              type="button"
              className="quick-access-btn quick-access-panel-btn"
              onClick={onToggleSourcePanel}
              aria-label={
                isSourcePanelOpen ? "بستن پنل منابع" : "باز کردن پنل منابع"
              }
              title={
                isSourcePanelOpen ? "بستن پنل منابع" : "باز کردن پنل منابع"
              }
            >
              {isSourcePanelOpen ? (
                <svg
                  className="ui-svg-icon panel-toggle-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              ) : (
                <svg
                  className="ui-svg-icon panel-toggle-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SourceQuickAccess;